import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Button,
   Box,
   Typography,
   Alert,
   CircularProgress,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Chip,
   IconButton,
} from '@mui/material';
import {
   Close as CloseIcon,
   Send as SendIcon,
   PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { RelationshipType, relationshipLabels } from '../../types/Relationship';
import { useUser } from '../../contexts/UserContext';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

interface NetworkInviteModalProps {
   open: boolean;
   onClose: () => void;
   onInviteSent?: () => void;
}

const NetworkInviteModal: React.FC<NetworkInviteModalProps> = ({ open, onClose, onInviteSent }) => {
   const { currentUser } = useUser();
   const [email, setEmail] = useState('');
   const [message, setMessage] = useState('');
   const [relationshipType, setRelationshipType] = useState<RelationshipType>('friend');
   const [notes, setNotes] = useState('');
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);
   const [userProfile, setUserProfile] = useState<{
      title?: string;
      companyName?: string;
   }>({});

   // Fetch user profile data when modal opens
   useEffect(() => {
      const fetchUserProfile = async () => {
         if (!open || !currentUser?.uid) return;

         try {
            const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
            if (profileDoc.exists()) {
               const profileData = profileDoc.data();
               setUserProfile({
                  title: profileData.title || '',
                  companyName: profileData.companyName || '',
               });
            }
         } catch (error) {
            console.error('Error fetching user profile:', error);
         }
      };

      fetchUserProfile();
   }, [open, currentUser?.uid]);

   const handleClose = () => {
      if (!loading) {
         setEmail('');
         setMessage('');
         setRelationshipType('friend');
         setNotes('');
         setError(null);
         setSuccess(null);
         onClose();
      }
   };

   const validateEmail = (email: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
   };

   const handleSendInvite = async () => {
      if (!currentUser) {
         setError('You must be logged in to send invitations');
         return;
      }

      if (!email.trim()) {
         setError('Please enter an email address');
         return;
      }

      if (!validateEmail(email.trim())) {
         setError('Please enter a valid email address');
         return;
      }

      if (email.trim().toLowerCase() === currentUser.email?.toLowerCase()) {
         setError('You cannot invite yourself');
         return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
         const headers = await getAuthHeaders();
         const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/network/invite`,
            {
               fromUserId: currentUser.uid,
               fromUserName: currentUser.displayName || 'Unknown User',
               fromUserEmail: currentUser.email,
               fromUserPhoto: currentUser.photoURL,
               fromUserTitle: userProfile.title,
               fromUserCompany: userProfile.companyName,
               toUserEmail: email.trim(),
               relationshipType,
               relationshipLabel: relationshipLabels[relationshipType],
               message: message.trim() || undefined,
               notes: notes.trim() || undefined,
            },
            { headers }
         );

         if (response.data.success) {
            const successMessage = response.data.emailSent
               ? 'Network invitation sent successfully!'
               : 'Network invitation created! (Email notification could not be sent - please share the invitation link manually)';

            setSuccess(successMessage);
            setEmail('');
            setMessage('');
            setRelationshipType('friend');
            setNotes('');

            // Call the callback to refresh network requests
            if (onInviteSent) {
               onInviteSent();
            }

            // Close modal after a longer delay if email wasn't sent (so user can read the message)
            setTimeout(
               () => {
                  handleClose();
               },
               response.data.emailSent ? 2000 : 4000
            );
         } else {
            throw new Error(response.data.error || 'Failed to send invitation');
         }
      } catch (err: any) {
         console.error('Error sending network invitation:', err);
         const errorMessage =
            err.response?.data?.error ||
            err.message ||
            'Failed to send network invitation. Please try again.';
         
         setError(errorMessage);

         // If the error is about an existing invitation, refresh the network requests
         // so the user can see it in their pending list
         if (
            errorMessage.includes('already exists') ||
            errorMessage.includes('pending invitation')
         ) {
            if (onInviteSent) {
               onInviteSent();
            }
         }
      } finally {
         setLoading(false);
      }
   };

   return (
      <Dialog
         open={open}
         onClose={handleClose}
         maxWidth='sm'
         fullWidth
         PaperProps={{
            sx: { borderRadius: 2 },
         }}
      >
         <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
         >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <PersonAddIcon color='primary' />
               <Typography variant='h6'>Invite Someone to Your Network</Typography>
            </Box>
            <IconButton onClick={handleClose} disabled={loading}>
               <CloseIcon />
            </IconButton>
         </DialogTitle>

         <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
               <Typography variant='body2' color='text.secondary'>
                  Send an invitation to connect with someone in your network. They'll receive an
                  email with a link to accept your invitation.
               </Typography>

               <TextField
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='Enter their email address'
                  fullWidth
                  required
                  disabled={loading}
                  error={!!error && error.includes('email')}
                  helperText={error && error.includes('email') ? error : ''}
               />

               <FormControl fullWidth disabled={loading}>
                  <InputLabel>Relationship Type</InputLabel>
                  <Select
                     value={relationshipType}
                     onChange={(e) => setRelationshipType(e.target.value as RelationshipType)}
                     label='Relationship Type'
                  >
                     {Object.entries(relationshipLabels).map(([type, label]) => (
                        <MenuItem key={type} value={type}>
                           {label}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>

               <TextField
                  label='Personal Message (Optional)'
                  multiline
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder='Add a personal message to your invitation...'
                  fullWidth
                  disabled={loading}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${message.length}/500 characters`}
               />

               <TextField
                  label='Private Notes (Optional)'
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Private notes about this person (e.g., 'We met at Vibe Code'). Only visible to you."
                  fullWidth
                  disabled={loading}
                  inputProps={{ maxLength: 300 }}
                  helperText={`Private notes for your reference only • ${notes.length}/300 characters`}
                  sx={{
                     '& .MuiInputLabel-root': { color: 'text.secondary' },
                     '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50',
                        '&:hover': { backgroundColor: 'grey.100' },
                     },
                  }}
               />

               {error && !error.includes('email') && (
                  <Alert severity='error' sx={{ mt: 1 }}>
                     {error}
                  </Alert>
               )}

               {success && (
                  <Alert severity='success' sx={{ mt: 1 }}>
                     {success}
                  </Alert>
               )}
            </Box>
         </DialogContent>

         <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button onClick={handleClose} disabled={loading} variant='outlined'>
               Cancel
            </Button>
            <Button
               onClick={handleSendInvite}
               disabled={loading || !email.trim()}
               variant='contained'
               startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
            >
               {loading ? 'Sending...' : 'Send Invitation'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default NetworkInviteModal;
